import { create } from "zustand";
import type { AddressFields } from "@/types/Address";
import type { BankDetails, SubmissionParams } from "@/types/Payment";
import { setValueAtPath } from "@/utils/setValueAtPath";
import { FormConfig } from "@/types/FormConfig";
import {
  clearDonationAmountSelection,
  loadDonationAmountSelection,
  saveDonationAmountSelection,
} from "./donationAmountSelectionStorage";
import { normalizePostcodeForCountry } from "@/utils/uk-postcode";
import type { UtmSubmission } from "@/types/components/UtmComponent";

export enum DonationType {
  OneOff = "one-off",
  Monthly = "monthly",
  Recurring = "recurring",
}

function acceptedDefaultAmount(
  amount: number | null,
  donationType: `${DonationType}` | null,
  hasCustomAmount: boolean,
  defaults: Partial<Record<DonationType, number | null>>,
): boolean | null {
  if (amount === null || donationType === null) return null;
  return !hasCustomAmount && defaults[donationType] === amount;
}

// #985: a migrated form carries `Recurring` where it used to carry `Monthly`.
// Every "is this a monthly donation?" check in the wizard means both types.
export const isRecurringLike = (
  donationType: `${DonationType}` | null,
): boolean =>
  donationType === DonationType.Monthly ||
  donationType === DonationType.Recurring;

// #984/#985: are platform (card/wallet) methods offered for this donation?
// The dedicated recurring type answers with its own switch; a plain monthly
// donation only counts as recurring when the form enables it. When platform
// methods aren't offered, Direct Debit is the only path — and the wizard skips
// the payment step, which leaves `paymentMethod` null. Every caller must ask
// this one selector; four hand-rolled copies had already drifted apart.
export const selectPlatformMethodsAvailable = (state: {
  donationType: `${DonationType}` | null;
  recurringDonationEnabled: boolean;
  recurringPaymentMethodsEnabled: boolean;
}): boolean =>
  state.donationType === DonationType.Recurring
    ? state.recurringPaymentMethodsEnabled
    : state.donationType !== DonationType.Monthly ||
      (state.recurringDonationEnabled && state.recurringPaymentMethodsEnabled);

// A monthly donation paid by anything other than Direct Debit is processed
// as a recurring donation, so an existing one must be looked up first.
// `paymentMethod` alone can't tell us that: when the form offers no platform
// methods the wizard skips the payment step entirely and leaves it null, so
// testing it on its own would fire the lookup for every Direct Debit donor on
// the migrated estate — something the legacy monthly form never did.
export const selectNeedsActiveRecurringCheck = (state: {
  donationType: `${DonationType}` | null;
  paymentMethod: string | null;
  recurringDonationEnabled: boolean;
  recurringPaymentMethodsEnabled: boolean;
}): boolean =>
  isRecurringLike(state.donationType) &&
  state.recurringDonationEnabled &&
  selectPlatformMethodsAvailable(state) &&
  state.paymentMethod !== "DD";

export const resolveDonationType = (state: {
  donationType: `${DonationType}` | null;
  paymentMethod: string | null;
  recurringDonationEnabled: boolean;
  recurringPaymentMethodsEnabled: boolean;
}): `${DonationType}` | null => {
  if (!isRecurringLike(state.donationType)) return state.donationType;
  if (state.paymentMethod === "DD" || !selectPlatformMethodsAvailable(state))
    return DonationType.Monthly;
  return DonationType.Recurring;
};

export type PersonalDetails = {
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  confirmEmail: string;
};

export type CardImage = {
  src: string;
  alt: string;
};

export type CardType = "" | "none" | "post" | "e-card";

export type CardSelector = {
  cardType: CardType;
  cardImage: CardImage;
  celebrationType: string;
  honouringForename: string;
  honouringSurname: string;
  sendCardToTitle: string;
  sendCardToForename: string;
  sendCardToSurname: string;
  postCardAddress: AddressFields;
  recipient: {
    email: string;
    confirmEmail: string;
  };
  message: string;
};

export type DonationFormStateValues = {
  activeFormId: number | null;
  gatewayPayload: Record<string, unknown>;
  donationType: `${DonationType}` | null;
  donationAmount: number | null;
  donationAmountAcceptedDefault: boolean | null;
  defaultDonationAmounts: Partial<Record<DonationType, number | null>>;
  donationOneOffType: string | null;
  isUKTaxPayer: boolean | null;
  personalDetails: PersonalDetails;
  bankDetails: BankDetails;
  donationSource: string;
  onBehalf: string;
  organisationName: string;
  cardEnabled: boolean;
  paymentMethod: string | null;
  hasCustomAmount: boolean;
  // While the donor types or steps their own amount it is not final yet, so
  // the payment session waits for them to leave the field.
  isEditingCustomAmount: boolean;
  signUpFrontline: boolean;
  address: AddressFields;
  isManualAddress: boolean;
  addressVerificationStatus: string;
  cardSelector: CardSelector;
  isSubmitted: boolean;
  recurringDonationEnabled: boolean;
  recurringDirectDebitEnabled: boolean;
  recurringPaymentMethodsEnabled: boolean;
  recurringDonationFrequencies: string[];
  utm: UtmSubmission;
};

export type DonationFormState = DonationFormStateValues & {
  setFieldValue: (path: string, value: unknown) => void;
  setDonationType: (tabName: `${DonationType}`) => void;
  setDonationAmount: (amount: null | number) => void;
  setDonationOneOffType: (type: null | string) => void;
  setUKTaxPayer: (isUKTaxPayer: boolean | null) => void;
  reset: () => void;
  clearPersistedDonationAmount: () => void;
  setPersonalDetails: (personalDetails: PersonalDetails) => void;
  setBankDetails: (bankPayment: Partial<BankDetails>) => void;
  setDonationSource: (source: string) => void;
  setOnBehalf: (onBehalf: string) => void;
  setOrganisationName: (organisationName: string) => void;
  setCardEnabled: (enabled: boolean) => void;
  setPaymentMethod: (method: string | null) => void;
  setHasCustomAmount: (hasCustomAmount: boolean) => void;
  setEditingCustomAmount: (isEditingCustomAmount: boolean) => void;
  setAddress: (address: AddressFields) => void;
  setSignUpFrontline: (signUpFrontline: boolean) => void;
  getSubmissionFields: () => SubmissionParams["fields"];
  setGatewayPayload: (gatewayPayload: Record<string, unknown>) => void;
  setIsManualAddress: (isManualAddress: boolean) => void;
  setAddressVerificationStatus: (status: string) => void;
  getPayment: () => {
    gatewayPayload: Record<string, unknown>;
  } | null;
  init: (formConfig: FormConfig, utm?: UtmSubmission) => void;
};

const initialState: DonationFormStateValues = {
  activeFormId: null,
  gatewayPayload: {},
  isSubmitted: false,
  isManualAddress: false,
  addressVerificationStatus: "",
  donationType: null,
  donationAmount: null,
  donationAmountAcceptedDefault: null,
  defaultDonationAmounts: {},
  donationOneOffType: null,
  isUKTaxPayer: null,
  cardEnabled: false,
  paymentMethod: null,
  personalDetails: {
    title: "",
    firstName: "",
    lastName: "",
    email: "",
    confirmEmail: "",
  },
  bankDetails: {
    account_holder: "",
    account_number: "",
    sort_code: "",
    start_date: "",
  },
  donationSource: "",
  onBehalf: "",
  organisationName: "",
  hasCustomAmount: false,
  isEditingCustomAmount: false,
  signUpFrontline: false,
  address: {
    "address-line-1": "",
    "address-line-2": "",
    city: "",
    country: "",
    "postal-code": "",
    region: "",
  },
  cardSelector: {
    cardType: "",
    celebrationType: "",
    cardImage: {
      src: "",
      alt: "",
    },
    honouringForename: "",
    honouringSurname: "",
    sendCardToTitle: "",
    sendCardToForename: "",
    sendCardToSurname: "",
    postCardAddress: {
      "address-line-1": "",
      "address-line-2": "",
      city: "",
      region: "",
      "postal-code": "",
      country: "",
    },
    recipient: {
      email: "",
      confirmEmail: "",
    },
    message: "",
  },
  recurringDonationEnabled: false,
  recurringDirectDebitEnabled: true,
  recurringPaymentMethodsEnabled: true,
  recurringDonationFrequencies: ["monthly"],
  utm: {},
};

export const useDonationFormStore = create<DonationFormState>((set, get) => ({
  ...initialState,
  init: (formConfig: FormConfig, utm = {}) => {
    const amountComponent = formConfig.components.find(
      (component) => component.name === "amount",
    );
    const donationTypeDefault =
      amountComponent?.config?.defaults?.["donation-type"];
    const oneOffAmountDefault =
      amountComponent?.config?.defaults?.["one-off-donation-amount"];
    const monthlyAmountDefault =
      amountComponent?.config?.defaults?.["monthly-donation-amount"];
    const recurringAmountDefault =
      amountComponent?.config?.defaults?.["recurring-donation-amount"];
    const recurringDonationEnabled =
      amountComponent?.config?.["recurring-donation-enabled"] === true;
    // #984: recurring payment-type switches. Absent (older configs) defaults to
    // enabled to preserve prior behaviour; only an explicit `false` disables.
    const recurringDirectDebitEnabled =
      amountComponent?.config?.["recurring-donation-direct-debit-enabled"] !==
      false;
    const recurringPaymentMethodsEnabled =
      amountComponent?.config?.[
        "recurring-donation-payment-methods-enabled"
      ] !== false;
    const recurringDonationFrequencies = amountComponent?.config?.[
      "recurring-donation-frequencies"
    ]?.length
      ? amountComponent.config["recurring-donation-frequencies"]
      : ["monthly"];

    const donationIntentComponent = formConfig.components.find(
      (component) => component.name === "donation_intent",
    );
    const donationIntentDefault =
      donationIntentComponent?.config?.defaults?.["donation-intent"];

    const donationSourceComponent = formConfig.components.find(
      (component) => component.name === "donation_source",
    );
    const donationSourceDefault =
      donationSourceComponent?.config?.defaults?.["donation-source"];

    const amountsByType = {
      [DonationType.OneOff]:
        amountComponent?.config["one-off-donation-amounts"]?.map(
          (item) => item.amount,
        ) ?? [],
      [DonationType.Monthly]:
        amountComponent?.config["monthly-donation-amounts"]?.map(
          (item) => item.amount,
        ) ?? [],
      [DonationType.Recurring]:
        amountComponent?.config["recurring-donation-amounts"]?.map(
          (item) => item.amount,
        ) ?? [],
    };

    const defaultDonationAmounts = {
      [DonationType.OneOff]: oneOffAmountDefault,
      [DonationType.Monthly]: monthlyAmountDefault,
      [DonationType.Recurring]: recurringAmountDefault,
    };

    const existingState = get();
    const storedSelection = loadDonationAmountSelection(formConfig.formId);
    const retainedSelection =
      existingState.activeFormId === formConfig.formId &&
      existingState.donationAmount !== null &&
      existingState.donationType !== null
        ? {
            donationType: existingState.donationType,
            amount: existingState.donationAmount,
            hasCustomAmount: existingState.hasCustomAmount,
          }
        : storedSelection;
    const restoredType = retainedSelection?.donationType as
      | DonationType
      | undefined;
    const restoredAmounts = restoredType ? amountsByType[restoredType] : [];
    const minimumByType = {
      [DonationType.OneOff]:
        amountComponent?.config["one-off-donation-amount-min"] ?? 1,
      [DonationType.Monthly]:
        amountComponent?.config["monthly-donation-amount-min"] ?? 1,
      [DonationType.Recurring]:
        amountComponent?.config["recurring-donation-amount-min"] ?? 1,
    };
    const restoredMinimum = restoredType ? minimumByType[restoredType] : 1;
    const enabledTypes = {
      [DonationType.OneOff]:
        amountComponent?.config["one-off-donation-enabled"] === true,
      [DonationType.Monthly]:
        amountComponent?.config["monthly-donation-enabled"] === true,
      [DonationType.Recurring]:
        recurringDonationEnabled &&
        amountComponent?.config["monthly-donation-enabled"] === false &&
        !!amountComponent?.config["recurring-donation-label"] &&
        restoredAmounts.length > 0,
    };
    const customAmountsEnabled = restoredType
      ? amountComponent?.config[`${restoredType}-donation-other`] === true
      : false;
    const canRestore =
      retainedSelection !== null &&
      retainedSelection !== undefined &&
      !!restoredType &&
      enabledTypes[restoredType] &&
      retainedSelection.amount >= restoredMinimum &&
      (retainedSelection.hasCustomAmount
        ? customAmountsEnabled
        : restoredAmounts.includes(retainedSelection.amount));
    const defaultDonationType = donationTypeDefault
      ? (donationTypeDefault as DonationType)
      : null;
    const donationType = canRestore ? restoredType : defaultDonationType;
    const donationAmount = canRestore ? retainedSelection.amount : null;
    const hasCustomAmount = canRestore
      ? retainedSelection.hasCustomAmount
      : false;

    if (retainedSelection && !canRestore) {
      clearDonationAmountSelection(formConfig.formId);
    }

    // #1264: DonationIntent syncs cardEnabled only on a click or for a single
    // option, so a preselected default must carry its card setting from here.
    // The intent applies to one-off donations only.
    const defaultIntentOption = donationIntentComponent?.config?.[
      "donation-intent"
    ]?.find((option) => option.key === donationIntentDefault);
    const cardEnabled =
      donationType === DonationType.OneOff &&
      defaultIntentOption?.["card-enabled"] === true;

    set({
      activeFormId: formConfig.formId,
      donationType,
      donationAmount,
      donationAmountAcceptedDefault: acceptedDefaultAmount(
        donationAmount,
        donationType,
        hasCustomAmount,
        defaultDonationAmounts,
      ),
      defaultDonationAmounts,
      donationOneOffType: donationIntentDefault ?? null,
      cardEnabled,
      donationSource: donationSourceDefault ?? "",
      hasCustomAmount: hasCustomAmount,
      recurringDonationEnabled,
      recurringDirectDebitEnabled,
      recurringPaymentMethodsEnabled,
      recurringDonationFrequencies,
      utm,
    });
  },
  setFieldValue: (path, value) => {
    const currentState = get();
    const updatedState = setValueAtPath(currentState, path, value);
    set(updatedState);
  },
  setGatewayPayload: (gatewayPayload) => set({ gatewayPayload }),
  setDonationType: (type) =>
    set({
      donationType: type,
      cardEnabled: false,
      donationOneOffType: null,
      paymentMethod: null,
      gatewayPayload: {},
    }),
  setDonationAmount: (amount) => {
    const state = get();
    const donationAmountAcceptedDefault = acceptedDefaultAmount(
      amount,
      state.donationType,
      state.hasCustomAmount,
      state.defaultDonationAmounts,
    );

    set({ donationAmount: amount, donationAmountAcceptedDefault });

    if (!state.activeFormId) return;
    if (amount === null || state.donationType === null) {
      clearDonationAmountSelection(state.activeFormId);
      return;
    }

    saveDonationAmountSelection(state.activeFormId, {
      donationType: state.donationType,
      amount,
      hasCustomAmount: state.hasCustomAmount,
    });
  },
  setDonationOneOffType: (type) => set({ donationOneOffType: type }),
  setUKTaxPayer: (isUKTaxPayer) => set({ isUKTaxPayer }),
  reset: () => {
    const formId = get().activeFormId;
    if (formId) clearDonationAmountSelection(formId);
    set(initialState);
  },
  clearPersistedDonationAmount: () => {
    const formId = get().activeFormId;
    if (formId) clearDonationAmountSelection(formId);
  },
  setPersonalDetails: (personalDetails) => set({ personalDetails }),
  setBankDetails: (bankDetails) => {
    const currentBankDetails = get().bankDetails;
    set({
      bankDetails: {
        ...currentBankDetails,
        ...bankDetails,
      },
    });
  },
  setDonationSource: (source) => set({ donationSource: source }),
  setOnBehalf: (onBehalf) => set({ onBehalf }),
  setOrganisationName: (organisationName) => set({ organisationName }),
  setCardEnabled: (enabled) => set({ cardEnabled: enabled }),
  setPaymentMethod: (method) => set({ paymentMethod: method }),
  setHasCustomAmount: (hasCustomAmount) => set({ hasCustomAmount }),
  setEditingCustomAmount: (isEditingCustomAmount) =>
    set({ isEditingCustomAmount }),
  setAddress: (address) => set({ address }),
  setSignUpFrontline: (signUpFrontline) => set({ signUpFrontline }),
  setIsManualAddress: (isManualAddress) => set({ isManualAddress }),
  setAddressVerificationStatus: (addressVerificationStatus) =>
    set({ addressVerificationStatus }),
  getPayment: () => {
    const gatewayPayload = get().gatewayPayload;

    if (Object.keys(gatewayPayload).length === 0) {
      return null;
    }

    return {
      gatewayPayload,
    };
  },
  getSubmissionFields: (): SubmissionParams["fields"] => {
    set({ isSubmitted: true });

    const {
      donationOneOffType,
      isUKTaxPayer,
      cardEnabled,
      bankDetails,
      donationType,
      donationSource,
      paymentMethod,
      recurringDonationEnabled,
      recurringPaymentMethodsEnabled,
      recurringDonationFrequencies,
      utm,
    } = get();

    const cardSelectorState = get().cardSelector;
    const cardType = cardSelectorState.cardType;

    const cardSelector = {
      card_type: cardType,
      celebration_type: cardSelectorState.celebrationType,
      ...(cardType !== "none" && {
        selected_card: cardSelectorState.cardImage?.src ?? "",
        selected_card_title: cardSelectorState.cardImage?.alt ?? "",
      }),
      honoree: {
        forename: cardSelectorState.honouringForename,
        surname: cardSelectorState.honouringSurname,
      },
      ...(cardType !== "none" && {
        recipient: {
          title: cardSelectorState.sendCardToTitle,
          forename: cardSelectorState.sendCardToForename,
          surname: cardSelectorState.sendCardToSurname,
          email: cardSelectorState.recipient.email,
          ...(cardSelectorState.recipient.confirmEmail && {
            confirm_email: cardSelectorState.recipient.confirmEmail,
          }),
          address_line_1: cardSelectorState.postCardAddress["address-line-1"],
          address_line_2: cardSelectorState.postCardAddress["address-line-2"],
          city: cardSelectorState.postCardAddress.city,
          postal_code: normalizePostcodeForCountry(
            cardSelectorState.postCardAddress["postal-code"],
            cardSelectorState.postCardAddress.country,
          ),
          country: cardSelectorState.postCardAddress.country,
        },
      }),
      message: cardSelectorState.message,
    };

    const resolvedDonationType = resolveDonationType({
      donationType,
      paymentMethod,
      recurringDonationEnabled,
      recurringPaymentMethodsEnabled,
    })!;
    const useBankDetails = resolvedDonationType === DonationType.Monthly;
    const treatAsRecurring = resolvedDonationType === DonationType.Recurring;

    const submission = {
      amount: {
        amount: get().donationAmount!,
        donation_type: resolvedDonationType,
        ...(treatAsRecurring && {
          frequency: recurringDonationFrequencies[0] ?? "monthly",
        }),
      },
      ...(donationOneOffType && {
        donation_intent: {
          donation_intent: donationOneOffType,
        },
      }),
      gift_aid: {
        gift_aid: donationSource === "own-money" && !!isUKTaxPayer,
      },
      personal_details: {
        title: get().personalDetails.title,
        first_name: get().personalDetails.firstName,
        last_name: get().personalDetails.lastName,
        email: get().personalDetails.email,
      },
      your_address: {
        address_line_1: get().address["address-line-1"],
        address_line_2: get().address["address-line-2"],
        city: get().address.city,
        region: get().address.region,
        postal_code: normalizePostcodeForCountry(
          get().address["postal-code"],
          get().address.country,
        ),
        country: get().address.country,
        ...(get().addressVerificationStatus && {
          verification_status: get().addressVerificationStatus,
        }),
      },
      ...(cardEnabled && { card_selector: cardSelector }),
      sign_up_to_frontline: {
        sign_up_frontline: get().signUpFrontline,
      },
      donation_source: {
        donation_source: donationSource,
        donation_behalf: get().onBehalf,
        donation_org_name: get().organisationName,
      },
      ...(useBankDetails && {
        bank_details: {
          account_holder: bankDetails.account_holder,
          account_number: bankDetails.account_number,
          sort_code: bankDetails.sort_code,
          start_date: bankDetails.start_date,
        },
      }),
      ...(!useBankDetails &&
        paymentMethod && {
          payment_methods: {
            payment_method: paymentMethod,
          },
        }),
      ...(Object.keys(utm).length > 0 && { utm }),
    };

    return submission;
  },
}));

export function getCurrentStateValues(): DonationFormStateValues {
  const state = useDonationFormStore.getState();
  return {
    activeFormId: state.activeFormId,
    gatewayPayload: state.gatewayPayload,
    donationType: state.donationType,
    donationAmount: state.donationAmount,
    donationAmountAcceptedDefault: state.donationAmountAcceptedDefault,
    defaultDonationAmounts: state.defaultDonationAmounts,
    donationOneOffType: state.donationOneOffType,
    isUKTaxPayer: state.isUKTaxPayer,
    personalDetails: state.personalDetails,
    bankDetails: state.bankDetails,
    donationSource: state.donationSource,
    onBehalf: state.onBehalf,
    organisationName: state.organisationName,
    cardEnabled: state.cardEnabled,
    paymentMethod: state.paymentMethod,
    hasCustomAmount: state.hasCustomAmount,
    isEditingCustomAmount: state.isEditingCustomAmount,
    signUpFrontline: state.signUpFrontline,
    address: state.address,
    isManualAddress: state.isManualAddress,
    addressVerificationStatus: state.addressVerificationStatus,
    cardSelector: state.cardSelector,
    isSubmitted: state.isSubmitted,
    recurringDonationEnabled: state.recurringDonationEnabled,
    recurringDirectDebitEnabled: state.recurringDirectDebitEnabled,
    recurringPaymentMethodsEnabled: state.recurringPaymentMethodsEnabled,
    recurringDonationFrequencies: state.recurringDonationFrequencies,
    utm: state.utm,
  };
}
